import * as React from "react";
import { 
  Play, Loader2, CheckCircle, XCircle, Database, BarChart3, AlertTriangle
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const JobCard = ({ title, description, icon: Icon, color, jobId, runEndpoint, onComplete }) => {
  const [status, setStatus] = React.useState(null);
  const [isRunning, setIsRunning] = React.useState(false);
  const pollRef = React.useRef(null);

  React.useEffect(() => {
    loadStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const loadStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/percentile-v2/job-status/${jobId}`, { withCredentials: true });
      setStatus(res.data);
      if (res.data.status === "running") { setIsRunning(true); startPolling(); }
    } catch (e) {}
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/admin/percentile-v2/job-status/${jobId}`, { withCredentials: true });
        setStatus(res.data);
        if (res.data.status !== "running") {
          setIsRunning(false);
          clearInterval(pollRef.current);
          if (res.data.status === "completed") toast.success(`${title} completado`);
          else toast.error(`Error en ${title}`);
          if (onComplete) onComplete();
        }
      } catch (e) {}
    }, 3000);
  };

  const handleRun = async () => {
    setIsRunning(true);
    try {
      await axios.post(`${API}${runEndpoint}`, {}, { withCredentials: true });
      toast.info(`${title} iniciado...`);
      startPolling();
    } catch (error) {
      setIsRunning(false);
      toast.error(error.response?.data?.detail || "Error al iniciar");
    }
  };

  const result = status?.result;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Icon className={`h-4 w-4 ${color}`} />
              {title}
            </CardTitle>
            <CardDescription className="mt-1">{description}</CardDescription>
          </div>
          <Button onClick={handleRun} disabled={isRunning} className="bg-cyan-600 hover:bg-cyan-700">
            {isRunning ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Ejecutando...</> : <><Play className="h-4 w-4 mr-2" />{title}</>}
          </Button>
        </div>
      </CardHeader>
      {status && status.status !== "never_run" && (
        <CardContent className="pt-0">
          <div className={`p-3 rounded-lg border text-sm ${
            status.status === "running" ? "bg-blue-50 border-blue-200" :
            status.status === "completed" ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
          }`}>
            <div className="flex items-center gap-2 mb-1">
              {status.status === "running" && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
              {status.status === "completed" && <CheckCircle className="h-4 w-4 text-emerald-600" />}
              {status.status === "error" && <XCircle className="h-4 w-4 text-red-600" />}
              <span className="font-medium">{status.status === "running" ? "En ejecución..." : status.status === "completed" ? "Completado" : "Error"}</span>
            </div>
            {result && (
              <div className="text-xs text-slate-600 space-y-0.5">
                <p>{result.message}</p>
                {result.duration_seconds && <p>Duración: {result.duration_seconds}s</p>}
                {result.rating_breakdown && (
                  <p>{Object.entries(result.rating_breakdown).map(([k,v]) => `${k}: ${v}`).join(" | ")}</p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export const PercentileV2Admin = () => {
  return (
    <div className="space-y-6">
      <JobCard
        title="Recalcular Baseline"
        description="Calcula percentiles históricos (P10-P90) de los últimos 60 días, segmentado por día de la semana."
        icon={Database} color="text-cyan-500"
        jobId="pct_baseline_job"
        runEndpoint="/admin/percentile-v2/run-baseline"
      />
      <JobCard
        title="Ejecutar Análisis"
        description="Compara los datos de ayer contra el baseline de percentiles y clasifica cada métrica."
        icon={BarChart3} color="text-cyan-500"
        jobId="pct_analysis_job"
        runEndpoint="/admin/percentile-v2/run-analysis"
      />
    </div>
  );
};
